import type { EChartsOption } from 'echarts';

// Shared x/y axis + dataZoom layout for the Fct forecast and comparison charts.
// Both charts plot a dated category axis with a value axis that auto-fits the
// zoomed window, so the config lives here instead of being copy-pasted.
export function fctCategoryAxis(axisLabels: string[]): EChartsOption['xAxis'] {
  return {
    type: 'category',
    data: axisLabels,
    axisLabel: { fontSize: 12, color: '#6b7280' },
    axisTick: { alignWithLabel: true },
  };
}

export function fctValueAxis(): EChartsOption['yAxis'] {
  return {
    type: 'value',
    // No fixed min: with filterMode 'filter' the axis rescales to the data in
    // the zoomed window, so the floor floats to the visible minimum.
    axisLabel: { fontSize: 12, color: '#6b7280' },
    splitLine: { lineStyle: { color: '#e5e7eb' } },
  };
}

// 'filter' drops out-of-window points so the y-axis auto-fits the visible range.
export const FCT_DATA_ZOOM: EChartsOption['dataZoom'] = [
  { type: 'slider', filterMode: 'filter', bottom: 5, height: 20 },
];

// Vertical dashed line marking the boundary between the historical period
// (actuals) and the forecast horizon. `boundaryLabel` is the category-axis label
// the line sits on — the first forecast point (or null to omit). Attached as an
// otherwise-empty line series so it shares the category axis.
export function fctForecastBoundaryMarkLine(boundaryLabel: string | null) {
  return {
    name: 'forecast-boundary',
    type: 'line' as const,
    data: [],
    silent: true,
    markLine: boundaryLabel
      ? {
          symbol: 'none' as const,
          lineStyle: { color: '#9ca3af', type: 'dashed' as const, width: 1.5 },
          label: { show: false },
          data: [{ xAxis: boundaryLabel }],
        }
      : undefined,
  };
}

type SeriesData = (number | null)[];

// Actuals rendered as a lollipop: a thin stem bar plus a dot head. Returns the
// two series so both Fct charts draw actuals identically.
export function fctActualsLollipop(name: string, data: SeriesData, color: string) {
  return [
    {
      name,
      type: 'bar' as const,
      data,
      barWidth: 2,
      itemStyle: { color },
      emphasis: { focus: 'series' as const },
      z: 2,
    },
    {
      name,
      type: 'scatter' as const,
      data,
      symbolSize: 6,
      itemStyle: { color },
      emphasis: { focus: 'series' as const },
      z: 3,
      tooltip: { show: false },
    },
  ];
}
